import { resolvePublicBaseUrl } from "../utils/resolvePublicBaseUrl";

export const CLIENT_ID =
    "1064115497372-pdg05barjshr9dfbv3gqu0ceeohu7nrv.apps.googleusercontent.com";

const isDev = import.meta.env.DEV;
/** אתר ציבורי — SSOT: `resolvePublicBaseUrl` (ENV, לא window.location). */
export const BaseUrl = resolvePublicBaseUrl({ skipTrace: true });

// התאמה לפרויקט הישן: תמיכה ב־VITE_API_BASE_URL ו־VITE_API_URL_DEV.
// ב־dev: ברירת מחדל "/api" (פרוקסי Vite → backend ב־localhost:8080) — אותו origin כמו ה־SPA, בלי CORS.
// ב־production: VITE_API_BASE_URL / VITE_API_URL > fallback https://api.hotelsriver.com
const baseFromEnv = (import.meta.env.VITE_API_BASE_URL && String(import.meta.env.VITE_API_BASE_URL).trim() !== "")
  ? String(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, "")
  : "";
const devUrl =
  baseFromEnv ||
  import.meta.env.VITE_API_URL_DEV ||
  (isDev ? "/api" : "");
const prodUrl = baseFromEnv || import.meta.env.VITE_API_URL || "https://api.hotelsriver.com";
export const ApiReqBaseUrl = isDev ? (devUrl || "/api") : prodUrl;

/**
 * Flask routes בנתיב root של ה־API host (ללא מקטע /api נוסף כשה־base הוא URL מלא).
 * אותם נתיבים כמו `${ApiReqBaseUrl}/futureOrdersCommission` בפרוד — תואם ל־Vite proxy ב־dev (/api/... → rewrite ל־Flask).
 * ממופה ל־endpoints ארנק/פיננסים בדשבורד וב־Finance flow.
 */
export const LEGACY_FLASK_WALLET_PIPELINE_PATHS = new Set([
  "/getBasicFinancials",
  "/futureOrdersCommission",
  "/availableOrdersCommission",
  "/ordersCommissionHistory",
  "/getFinanceByEmail",
  "/getRecentOrdersAll",
  "/getHrCommissionSystemTotal",
  "/getHrCommissionSystemOrders",
]);

function pathnameOnly(path) {
  const s = String(path || "").trim();
  const i = s.indexOf("?");
  return i < 0 ? s : s.slice(0, i);
}

function isLegacyWalletPipelinePath(fullPath) {
  const p = pathnameOnly(fullPath);
  if (!p.startsWith("/")) return false;
  return LEGACY_FLASK_WALLET_PIPELINE_PATHS.has(p);
}

function isExplicitApiPrefixedPath(fullPath) {
  const p = pathnameOnly(fullPath);
  return p.startsWith("/api/") || p === "/api";
}

function joinWithRelativeApiBase(base, pathAfterApiRemoval) {
  return `${base}${pathAfterApiRemoval}`;
}

function computeAutoApiPrefixedFullPath(full) {
  const po = pathnameOnly(full);
  const rest = full.slice(po.length);
  return `/api${po}${rest}`;
}

function resolvedUrlIncludesApiPrefix(resolvedUrl) {
  try {
    const abs =
      resolvedUrl.startsWith("http://") || resolvedUrl.startsWith("https://")
        ? resolvedUrl
        : new URL(
            resolvedUrl,
            typeof window !== "undefined" ? window.location.href : "http://localhost"
          ).href;
    const u = new URL(abs);
    return u.pathname.startsWith("/api/") || u.pathname === "/api";
  } catch {
    return false;
  }
}

function resolvedUrlFinalHost(resolvedUrl) {
  try {
    const abs =
      resolvedUrl.startsWith("http://") || resolvedUrl.startsWith("https://")
        ? resolvedUrl
        : new URL(
            resolvedUrl,
            typeof window !== "undefined" ? window.location.href : "http://localhost"
          ).href;
    return new URL(abs).host;
  } catch {
    return null;
  }
}

function shouldEmitApiResolveTrace(branch, pathOnly) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return false;
  if (String(import.meta.env.VITE_SILENT_API_LOG || "").trim() === "1") return false;
  if (String(import.meta.env.VITE_SILENT_API_RESOLVE_TRACE || "").trim() === "1") {
    return false;
  }
  if (String(import.meta.env.VITE_API_RESOLVE_TRACE || "").trim() === "1") {
    return true;
  }
  if (branch === "legacy_wallet_pipeline_root") return true;
  if (branch === "explicit_api" && pathOnly.startsWith("/api/agent")) return true;
  return false;
}

/**
 * SSOT לבניית URL ל־API.
 * - נתיבי `/api/...` (blueprints) — נשארים עם הקידומת ב־prod; ב־dev עם base `/api` מוסרים כפילות.
 * - נתיבי ארנק/פיננסים ברשימת LEGACY_FLASK_WALLET_PIPELINE_PATHS — כמו בפרוד: `{base}/getBasicFinancials` בלי הזרקת /api על host מלא.
 * - שאר נתיבים — הזרקת `/api` לפני הנתיב (התנהגות קיימת; proxy ב־dev מסיר /api לרוב ה־Flask root).
 */
export function resolveApiUrl(flaskAbsolutePath) {
  const originalEndpoint = flaskAbsolutePath;
  const base = String(ApiReqBaseUrl).replace(/\/$/, "");
  let full = String(flaskAbsolutePath || "").trim();
  if (!full) full = "/";
  if (!full.startsWith("/")) full = `/${full}`;

  const pathOnly = pathnameOnly(full);
  let resolvedUrl;
  let branch;

  if (isExplicitApiPrefixedPath(full)) {
    branch = "explicit_api";
    if (base === "/api" && pathOnly.startsWith("/api/")) {
      resolvedUrl = joinWithRelativeApiBase(base, full.slice(4));
    } else {
      resolvedUrl = `${base}${full}`;
    }
  } else if (isLegacyWalletPipelinePath(full)) {
    branch = "legacy_wallet_pipeline_root";
    resolvedUrl = `${base}${full}`;
  } else {
    branch = "auto_api_prefix";
    let p = computeAutoApiPrefixedFullPath(full);
    if (base === "/api" && pathnameOnly(p).startsWith("/api/")) {
      resolvedUrl = joinWithRelativeApiBase(base, p.slice(4));
    } else {
      resolvedUrl = `${base}${p}`;
    }
  }

  const includesApiPrefix = resolvedUrlIncludesApiPrefix(resolvedUrl);
  const finalHost = resolvedUrlFinalHost(resolvedUrl);

  if (shouldEmitApiResolveTrace(branch, pathOnly)) {
    console.info("[API_RESOLVE_TRACE]", {
      originalEndpoint,
      resolvedUrl,
      baseUrl: base,
      finalHost,
      includesApiPrefix,
      branch,
      environment: import.meta.env.MODE,
    });
  }

  return resolvedUrl;
}

// לבדיקת BE ישיר (ללא proxy): הגדר VITE_API_BASE_URL למשל https://api.hotelsriver.com או URL מלא אחר.
if (isDev && import.meta.env.VITE_SILENT_API_LOG !== "1") {
    console.info("[HotelsRiver FE] ApiReqBaseUrl →", ApiReqBaseUrl);
}

export const GoogleMapSecretKey = "AIzaSyDhxJv4pkO852UnQgOEPqKQk-_UxJmE2AY";
export const IS_DEV = import.meta.env.VITE_IS_DEV === "1";

/** כבוי מפורש: VITE_SEARCH_RESULTS_ATTRACTIONS=0 (rollback מהיר). ברירת מחדל: מופעל. */
export function isSearchResultsAttractionsEnabled() {
    return String(import.meta.env.VITE_SEARCH_RESULTS_ATTRACTIONS || "").trim() !== "0";
}
